import { useEffect, useRef } from "react";
import { useLocation, useHistory } from "../Router";
import useCredits from "./useCredits";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { useTranslation } from "react-i18next";

// useRechargeRedirect: detects the Stripe Checkout success/cancel redirect
// (?recharged=1&session_id=... or ?recharged=0) on the current route, then:
//   - on success: synchronously confirms the recharge (grants credit if the
//     async checkout.session.completed webhook hasn't fired yet), refreshes the
//     balance, toasts success, and strips the query params from the URL.
//   - on cancel: toasts a neutral message and strips the params.
//
// This closes the race where the browser redirect arrives before the webhook,
// which would otherwise leave the balance at 0 and hard-block workspace
// creation right after the user paid. Safe to mount on any route that can be a
// recharge return target (workspaces, billing).
const useRechargeRedirect = () => {
  const location = useLocation();
  const history = useHistory();
  const credits = useCredits();
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const handledRef = useRef(null);

  useEffect(() => {
    if (!location?.search) return;
    const params = new URLSearchParams(location.search);
    const recharged = params.get("recharged");
    const sessionId = params.get("session_id");
    if (recharged === null) return;

    // Avoid double-handling the same redirect (StrictMode / re-renders).
    const sig = `${recharged}:${sessionId || ""}`;
    if (handledRef.current === sig) return;
    handledRef.current = sig;

    const cleanUrl = () => {
      try {
        history.replace({ search: "", pathname: location.pathname });
      } catch (e) {
        // swallow — non-critical
      }
    };

    if (recharged === "1" && sessionId) {
      AppToasty.show(
        t("credits.recharge.confirming", { defaultValue: "Confirming your payment…" }),
        { type: "normal" },
      );
      (async () => {
        try {
          const res = await credits.confirmRecharge({ sessionId });
          if (res?.granted || res?.confirmed) {
            AppToasty.show(
              t("credits.recharge.success", { amount: "" }).trim() ||
                t("credits.recharge.redirected", { defaultValue: "Credits added to your account." }),
              { type: "success" },
            );
          } else if (res?.grantReason === "already_granted") {
            // Already credited by the webhook — just refresh happened.
            AppToasty.show(
              t("credits.recharge.confirmed", { defaultValue: "Payment confirmed." }),
              { type: "success" },
            );
          } else if (res?.paymentStatus && res.paymentStatus !== "paid") {
            AppToasty.show(
              t("credits.recharge.notPaidYet", {
                defaultValue: "Payment not completed yet.",
              }),
              { type: "danger" },
            );
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log("[useRechargeRedirect] confirm failed", e);
          AppToasty.show(t("credits.recharge.failed"), { type: "danger" });
        } finally {
          cleanUrl();
        }
      })();
    } else if (recharged === "1") {
      // No session_id to confirm (legacy URL) — just refresh the balance.
      credits.refresh({ silent: true }).finally(cleanUrl);
    } else {
      AppToasty.show(
        t("credits.recharge.cancelled", { defaultValue: "Recharge cancelled." }),
        { type: "normal" },
      );
      cleanUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.search]);
};

export default useRechargeRedirect;
